import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowRight, MapPin, Truck, Package, ChevronDown } from 'lucide-react';
import CartItem from '../components/CartItem';
import Button from '../components/Button';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCart } from '../context/CartContext';
import { useZones } from '../hooks/useZones';
import { useAuth } from '../context/AuthContext';

const Cart = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { 
    items, 
    selectedZone, 
    setSelectedZone, 
    updateQuantity, 
    removeItem,
    subtotal,
    shippingCost,
    total,
    itemCount,
  } = useCart();
  const { zones, loading: zonesLoading } = useZones();
  
  const [showZoneSelector, setShowZoneSelector] = useState(false);

  const handleUpdateQuantity = (productId, quantity) => {
    updateQuantity(productId, quantity);
  };

  const handleRemoveItem = (productId) => {
    removeItem(productId);
  };

  const handleZoneSelect = (zone) => {
    setSelectedZone(zone);
    setShowZoneSelector(false);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    } else {
      navigate('/checkout');
    }
  };

  if (itemCount === 0) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-24 px-4">
        <div className="max-w-md mx-auto text-center py-16">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#F8F7F5] flex items-center justify-center">
            <ShoppingBag size={48} className="text-[#BCB8B1]" />
          </div>
          <h1 className="text-2xl font-display font-bold text-[#1B263B] mb-3">
            Tu carrito está vacío
          </h1>
          <p className="text-[#7A756F] mb-8">
            Agrega productos para comenzar tu pedido
          </p>
          <Link to="/productos">
            <Button size="lg">
              Ver productos
              <ArrowRight size={20} />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-40 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-display font-bold text-[#1B263B]">
            Tu Carrito
          </h1>
          <span className="text-[#7A756F]">
            {itemCount} {itemCount === 1 ? 'producto' : 'productos'}
          </span>
        </div>

        {/* Cart Items */}
        <div className="space-y-4 mb-6">
          {items.map((item) => (
            <CartItem
              key={item.product.id}
              item={item}
              onUpdateQuantity={handleUpdateQuantity}
              onRemove={handleRemoveItem}
            />
          ))}
        </div>

        {/* Zone Selector */}
        <Card className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MapPin size={20} className="text-primary" />
              <span className="font-semibold text-[#1B263B]">Zona de entrega</span>
            </div>
            {selectedZone && (
              <span className="text-sm text-success font-medium">
                ${selectedZone.cost.toLocaleString()}
              </span>
            )}
          </div>
          
          <button
            onClick={() => setShowZoneSelector(!showZoneSelector)}
            className="w-full flex items-center justify-between p-3 bg-[#F8F7F5] rounded-xl hover:bg-[#EFEDEA] transition-colors"
          >
            <span className={selectedZone ? 'text-[#1B263B]' : 'text-[#9A9590]'}>
              {selectedZone ? selectedZone.name : 'Selecciona tu zona'}
            </span>
            <ChevronDown 
              size={20} 
              className={`text-[#9A9590] transition-transform ${showZoneSelector ? 'rotate-180' : ''}`}
            />
          </button>

          {showZoneSelector && (
            <div className="mt-3 space-y-2 animate-fadeIn">
              {zonesLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                zones.map((zone) => (
                  <button
                    key={zone.id}
                    onClick={() => handleZoneSelect(zone)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-colors ${
                      selectedZone?.id === zone.id
                        ? 'border-primary bg-primary/5'
                        : 'border-[#EFEDEA] hover:border-primary/50'
                    }`}
                  >
                    <span className="text-[#1B263B]">{zone.name}</span>
                    <span className="font-semibold text-primary">
                      ${zone.cost.toLocaleString()}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}

          {selectedZone?.delivery_time && (
            <p className="mt-3 text-sm text-[#7A756F] flex items-center gap-1">
              <Truck size={14} />
              Tiempo estimado: {selectedZone.delivery_time}
            </p>
          )}
        </Card>

        {/* Summary */}
        <Card className="mb-6">
          <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4">
            Resumen del pedido
          </h2>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-[#7A756F]">Subtotal</span>
              <span className="font-medium text-[#1B263B]">
                ${subtotal.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-[#7A756F]">Envío</span>
              <span className="font-medium text-[#1B263B]">
                {selectedZone ? `$${shippingCost.toLocaleString()}` : 'Selecciona zona'}
              </span>
            </div>

            <div className="border-t border-[#EFEDEA] pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="font-display font-bold text-lg text-[#1B263B]">Total</span>
                <span className="font-display font-bold text-2xl text-primary">
                  ${total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Checkout Button - Fixed at bottom */}
        <div className="fixed bottom-20 left-0 right-0 px-4 bg-white border-t border-[#EFEDEA] py-4">
          <div className="max-w-2xl mx-auto">
            <Button
              size="lg"
              fullWidth
              onClick={handleCheckout}
              disabled={!selectedZone}
              className="gap-2"
            >
              {isAuthenticated ? 'Continuar' : 'Iniciar sesión para continuar'}
              <ArrowRight size={20} />
            </Button>
            {!selectedZone && (
              <p className="text-center text-sm text-error mt-2">
                Selecciona una zona de entrega para continuar
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
